import { split, last, get, head } from 'lodash';
import moment from 'moment';
import {
	amountLimit,
	invalidReferenceCharactersRegex,
	EMPTY_PAYMENT_ROW_COUNT,
	PAYMENTS_LIMIT,
	swiftFormat,
	invalidFasterPaymentsReferenceCharactersRegex,
	invalidBACSReferenceCharactersRegex,
	chapsReferenceMaxLength,
	referenceMaxLength,
	bacsReferenceMaxLength,
	currenciesForCalender,
	batchNameMaxLength,
	invalidRepeatingPaymentRegex,
} from '../config/';
import { whoPaysMapping, WHO_PAYS_TYPE_SHARED } from '../config/ift';
import { bacsLimitFrequencyMap } from './content';
import { getCurrencyDecimalPlaces } from '../utils/countryUtils';
import { isUserSelfApprover, isUserSelfApproverMulti } from '../utils/authoriserUtils';
import currencyMapping from '../config/currencyMapping';
import { getPaymentsList } from '../states/payments/getPaymentsList';
import { getChapsPaymentsList } from '../states/chaps/selectors';
import { getPayments } from '../states/bacsCredit/selectors';
import { getPage } from '../states/page/selectors';

const removeCommas = (amount = '') => String(amount).replace(/,/g, '');

export const setAmountWithDecimalPlace = (amount, decimalLimit) =>
	Number.parseFloat(amount).toLocaleString('en-GB', {
		minimumFractionDigits: decimalLimit,
		maximumFractionDigits: decimalLimit,
	}) || amount;

export const formatAmount = (amount, decimalLimit = 2) => {
	if (amount !== '') {
		return (
			Number.parseFloat(removeCommas(amount)).toLocaleString('en-GB', {
				minimumFractionDigits: decimalLimit,
				maximumFractionDigits: decimalLimit,
			}) || amount
		);
	}
	return amount;
};

export const fixAmount = (amount = '', decimalLimit = 2) => {
	const numericAmount = typeof amount === 'string' ? Number.parseFloat(removeCommas(amount)) : amount;

	if (Number.isNaN(numericAmount)) return '';

	return numericAmount.toLocaleString('en-GB', {
		minimumFractionDigits: decimalLimit,
		maximumFractionDigits: decimalLimit,
	});
};

export const getCurrencyPrefix = (currencyCode) => {
	const currencySignMapping = {
		USD: '$',
		EUR: '€',
		GBP: '£',
	};
	return get(currencySignMapping, currencyCode, '');
};

export const stripCurrencysIllegalCharacters = (amount, decimalLimit = 2) => {
	if (!amount) {
		return amount;
	}
	const cleanedAmount = amount.replace(/[^\d.]/g, '').replace(/\.{2,}/g, '.');

	const amtParts = split(cleanedAmount, '.');
	if (amtParts.length > 1 && last(amtParts).length > decimalLimit) {
		return cleanedAmount.slice(0, -(last(amtParts).length - decimalLimit));
	}

	return cleanedAmount;
};

export const isAmountWithinLimit = (amount, limit = amountLimit) => {
	const numericAmount = Number.parseFloat(amount);
	if (!Number.isNaN(numericAmount) && numericAmount > limit) return false;
	return true;
};
export const cleanSwiftFormatCharacters = (string) => string && string.replace(swiftFormat, '');

export const cleanIllegalHVPMCharacters = (string) =>
	string && string.replace(swiftFormat, '').trimLeft().substring(0, chapsReferenceMaxLength);
export const cleanIllegalFasterPaymentsCharacters = (string) =>
	string &&
	string.replace(invalidFasterPaymentsReferenceCharactersRegex, '').trimLeft().substring(0, referenceMaxLength);
export const cleanIllegalBACSCharacters = (string) =>
	string && string.replace(invalidBACSReferenceCharactersRegex, '').trimLeft().substring(0, bacsReferenceMaxLength);
export const cleanIllegalCharacters = (string) => string && string.replace(invalidReferenceCharactersRegex, '');
export const cleanIllegalCharactersNumeric = (string) => string && string.replace(/\D/g, '');

export const formatBacsLimit = ({ limit, limit_frequency }) => {
	if (!limit || !limit_frequency) {
		return null;
	}
	const limitCurrency = limit.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

	return `Limit £${limitCurrency} ${bacsLimitFrequencyMap[limit_frequency]}`;
};

export const getReference = (payees, id, isCHAPS = false) => {
	const highValueReference = get(
		payees.find((payee) => payee.id === id),
		'reference_high_value',
		undefined
	);
	const reference = get(
		payees.find((payee) => payee.id === id),
		'reference',
		undefined
	);
	return isCHAPS ? highValueReference || reference : reference;
};

export const getPayeeCount = (numberOfSelectedPayees, payments) =>
	numberOfSelectedPayees + (payments.length - EMPTY_PAYMENT_ROW_COUNT);

// eslint-disable-next-line no-confusing-arrow
export const canShowPayeeSelectLimitMsg = (numberOfSelectedPayees, payments = []) =>
	payments.length > 0
		? getPayeeCount(numberOfSelectedPayees, payments) >= PAYMENTS_LIMIT
		: numberOfSelectedPayees >= PAYMENTS_LIMIT;

// eslint-disable-next-line no-confusing-arrow
export const canDisablePayeeContinue = (numberOfSelectedPayees, payments = []) =>
	payments.length > 0
		? getPayeeCount(numberOfSelectedPayees, payments) > PAYMENTS_LIMIT
		: numberOfSelectedPayees > PAYMENTS_LIMIT;

export const isUserPaymentAuthoriser = (selectedAuthorisers = [], userId) =>
	selectedAuthorisers.some((authoriser) => authoriser.id === userId);

export const isUserSelectedSingleApprover = (authorisers, userId) =>
	isUserPaymentAuthoriser(authorisers, userId) && authorisers.length === 1;

export const getAuthorisationMessage = (authorisers = [], userId, authorisedByMsg = 'This will need authorised by') => {
	const authoriserNames = authorisers.map((authoriser) => authoriser.name).join(' and ');

	const authorisersExcludingSelfApprover = authorisers.filter((authoriser) => authoriser.id !== userId);
	const authoriserNamesExcludingSelfApprover = authorisersExcludingSelfApprover
		.map((authoriser) => authoriser.name)
		.join(' and ');

	if (authorisers.length) {
		if (isUserSelfApproverMulti(userId, authorisers)) {
			return `${authorisedByMsg} you and ${authoriserNamesExcludingSelfApprover}.`;
		}
		if (isUserSelfApprover(userId, authorisers)) {
			return 'This payment can be authorised by you';
		} else {
			return `${authorisedByMsg} ${authoriserNames}.`;
		}
	} else return '';
};

export const isFromAccountExoticCurrency = (fromCurrency) => !currenciesForCalender.includes(fromCurrency);

export const getCurrencySymbol = (currency) => ({ GBP: '£ ', USD: '$ ', EUR: '€ ' }[currency] || '');

export const getAmountWithCurrency = (currency, amount) => {
	if (amount === '') return '';
	const currencyDecimalPlaces = getCurrencyDecimalPlaces(currency);
	return `${getCurrencySymbol(currency)}${formatAmount(amount, currencyDecimalPlaces)} ${currency}`;
};

export const getWhoPaysLabel = (type) => {
	if (type === WHO_PAYS_TYPE_SHARED) {
		return get(
			whoPaysMapping.find(({ id }) => id === type),
			'label'
		);
	}
	return `Paid by ${get(
		whoPaysMapping.find(({ id }) => id === type),
		'label'
	)}`;
};
export const isPreBookedMandatory = (fromCurrency, sendCurrency) => {
	const currency = currencyMapping.find(
		(currency) => currency.from_currency === fromCurrency && currency.to_currency === sendCurrency
	);
	return get(currency, 'pre_booked_rate_mandatory', '') === 'Y';
};
export const getFilteredSubmissionDates = (submissionDates, isPreBookedRate) => {
	if (isPreBookedRate) {
		return [head(submissionDates)];
	}
	return submissionDates;
};

export const getDate = (payment) => {
	const date = moment(get(payment, 'date'));
	if (date.isBefore(moment())) return moment();
	return date;
};

export const getSelectedSubmissionDate = (tableData) => {
	const ratesSubmissionDate = get(
		tableData.find((entry) => entry.type === 'subDate'),
		'body',
		''
	);
	return moment(ratesSubmissionDate);
};

export const trimBatchName = (name) => name.substring(0, batchNameMaxLength);

export const cleanIllegalRepeatingPaymentCharacters = (value = '') =>
	String(value).replace(invalidRepeatingPaymentRegex, '');

export const updatePayeeSingularCountMessage = (content, paymentLimit = PAYMENTS_LIMIT) => ({
	...content,
	payeeSingularCountMessage: content.payeeSingularCountMessage.replace('150', paymentLimit),
});

export const isUrgentPayment = (isBeneficiaryEEACountry, sendCurrency) =>
	!isBeneficiaryEEACountry || (sendCurrency !== 'EUR' && isBeneficiaryEEACountry);

export const hasPreviousCopCheckPerformed = (payee) => get(payee, 'confirmation_of_payee.cop_status') === 'active';

export const hasAllRequiredCopFields = (payee) => {
	const {
		cop_account_name,
		cop_account_type,
		reference,
		to_account: { sort_code, account_number },
	} = payee;
	return Boolean(cop_account_name && cop_account_type && reference && sort_code && account_number);
};

export function getCopDetails({
	payee: {
		cop_account_name,
		cop_account_type,
		reference,
		to_account: { sort_code, account_number },
	},
	page,
	customerId,
	customerType,
	beneficiaryId,
	updatedRef,
}) {
	return {
		accountName: cop_account_name,
		accountNumber: account_number,
		sortCode: sort_code,
		accountType: cop_account_type,
		reference: updatedRef || reference,
		customerId,
		page,
		customerType,
		beneficiaryId,
	};
}
export const isCopCheckRequired = (oldValues, newValues) => {
	return (
		oldValues.sortCode !== newValues.sortCode ||
		oldValues.accountNumber !== newValues.accountNumber ||
		oldValues.reference !== newValues.reference ||
		oldValues.copType !== newValues.copType ||
		oldValues.name !== newValues.name
	);
};

export const formatCOPDetails = (payeeCopDetails) => {
	return payeeCopDetails
		? {
				payeeName: payeeCopDetails.CoPAccountName,
				payeeType: payeeCopDetails.CoPAccountType,
				result: {
					...(payeeCopDetails.CoPReasonCode && {
						cop_reason_code: payeeCopDetails.CoPReasonCode,
					}),
					...(payeeCopDetails.CopStatus && { cop_status: payeeCopDetails.CopStatus }),
				},
		  }
		: undefined;
};

export const getPaymentsRef = (state) => {
	const pageName = getPage(state);
	switch (pageName) {
		case 'Payments':
			return get(getPaymentsList(state), '[0].reference');
		case 'ChapsPayments':
			return get(getChapsPaymentsList(state), '[0].reference');
		case 'BacsCreditPayments':
			return get(getPayments(state), '[0].reference');
		default:
			return null;
	}
};

export const sanitizeDecimalOccurrences = (amount = '') => {
	const decimalOccurrences = amount.split('.').length - 1;
	return decimalOccurrences > 1 ? amount.substring(0, amount.lastIndexOf('.')) : amount;
};
